/**
 * 히어로(아바타) 파츠 카탈로그 + 검증 — FR-HERO-01/08/15.
 * ⚠️ 단일 정의: 클라(렌더·저장)와 서버(POST /api/hero 페이로드 검증)가 동일 화이트리스트를 쓴다.
 * 손상·주입 방지(S2-3): 외부에서 온 parts/base/palette 는 반드시 sanitizeHero 통과 후 사용.
 * 캐릭터는 IP(그레그·병아리)와 구분되는 "꼬마 탐험가" 세트(S2-1) — base = explorer_*.
 */
import type { HeroBase, SavedHero } from "./index";
import { DEFAULT_BADWORDS, filterNickname } from "./filter";

export const HERO_SCHEMA_VERSION = 1;
/** 코드에 쓰는 글자 — 혼동문자(I,L,O,U,0,1) 제외. **값은 정의한 여기서** 가져간다(순환 참조 방지). */
export const HERO_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";
export const HERO_CODE_LENGTH = 6;

// 익명 히어로 코드 — Base32(혼동문자 제외) 6자(FR-HERO-09). 식별자 아님.
/** 코드 생성. rand 주입 가능(서버는 crypto, 테스트는 결정론적). */
export function generateHeroCode(rand: () => number = Math.random): string {
  let out = "";
  for (let i = 0; i < HERO_CODE_LENGTH; i++) {
    const idx = Math.floor(rand() * HERO_CODE_ALPHABET.length) % HERO_CODE_ALPHABET.length;
    out += HERO_CODE_ALPHABET[idx];
  }
  return out;
}

// ⚠️ 알파벳은 함수 본문에서 읽는다(모듈 로드 시 순환참조로 undefined 가 되는 것 방지).
/**
 * 🔴 **이 함수는 「반듯한 모양」인지만 본다** — 대소문자를 가린다.
 *
 * 사람이 친 값을 바로 넣지 말 것. 폰 주소창은 소문자로 바꿔 놓는 일이 있고, 종이 카드를
 * 소문자로 옮겨 적기도 한다. 그러면 **멀쩡한 코드가 「없는 코드」가 된다** —
 * `heroCodeHint` 는 대문자로 바꿔 보고 「틀린 것 없음」이라 하고, 이쪽은 「틀렸다」고 해서
 * 화면이 **둘 다 아닌 말**을 하게 된다(2026-09-02 실측).
 *
 * 사람이 친 값은 **먼저 `parseHeroCode` 로 반듯하게** 만든 뒤 넘긴다.
 */
export function isValidHeroCode(code: unknown): code is string {
  if (typeof code !== "string" || code.length !== HERO_CODE_LENGTH) return false;
  for (const ch of code) {
    if (!HERO_CODE_ALPHABET.includes(ch)) return false;
  }
  return true;
}

// 베이스 + 몸/머리/소품/색상 각 3~5종(FR-HERO-01). a0 = 소품 없음.
// 레이어드 파츠 카탈로그(콘텐츠 주도 확장). 옵션 이미지는 관리자에서 hero.<카테고리>.<id> 로 업로드.
// 0번(b0/c0/o0/a0)은 보통 "없음/기본". 새 옵션은 카탈로그에 추가만 하면 됨(코드 무수정).
/**
 * 기본으로 들어 있는 것들. **여기 없다고 못 쓰는 게 아니다** — 운영자가 만든 것은
 * 매니페스트에서 찾아 쓴다(customHeroIds·customPartIds). 이 표가 하는 일은 둘뿐이다:
 *  ① 기본 제공분의 순서·기본값  ② 그림이 없어도 관리자 목록에 남겨 둘 것
 *
 * 예전엔 여기에 빈 자리(explorer_f~i·c5~c8·a5~a8)를 미리 뚫어 뒀다. 목록이 곧 검증이라
 * 자리를 늘리려면 코드를 고쳐야 했다 — **콘텐츠를 코드가 막고 있었다.**
 *
 * 몸(body)·옷(outfit)·색(palette)은 그대로 닫혀 있다. 통짜 룩 그림이 몸×옷 16장으로
 * 미리 그려져 있어서 새 id 를 넣어도 그릴 그림이 없다. 늘어나는 건 그림 한 장이 곧
 * 항목인 것들뿐이다 — 완성 캐릭터·모자·소품.
 */
export const HERO_CATALOG = {
  base: [
    "explorer_a", "explorer_b", "explorer_c", "explorer_d", "explorer_e",
    "custom",
  ] as const, // 기본 제공 5 + 직접 꾸미기. 운영자가 만든 것은 매니페스트에서 찾는다
  body: ["b1", "b2", "b3", "b4"] as const, // 아이 — 얼굴·머리카락·피부가 각각 다른 네 아이(룩 그림에 통째로 그려져 있다)
  // 모자·머리장식(c0=안 씀). 룩 위에 얹는다. 운영자가 만든 것은 여기 없고 매니페스트에 있다.
  hair: ["c0", "c1", "c2", "c3", "c4"] as const,
  outfit: ["o0", "o1", "o2", "o3"] as const, // 옷(o0=기본)
  // 소품(a0=없음, a4=경주 완주 한정 뱃지 FR-RALLY-10). 운영자가 만든 것은 매니페스트에 있다.
  acc: ["a0", "a1", "a2", "a3", "a4"] as const,
  palette: ["warm", "cool", "mint", "berry", "sand"] as const,
} as const;

/**
 * ## 운영자가 직접 만드는 빈 자리(c5~c8 · a5~a8)
 *
 * 새 모자를 하나 만들려고 개발자를 부르지 않아도 되게 **자리를 미리 비워 뒀다.**
 * 관리자 [➕ 직접 만들기]에서 그림을 올리면 그때부터 아이 화면의 선택지로 나온다.
 *
 * 목록을 **열어 두지 않고 빈 자리로 둔 이유**: 파츠 문자열은 바깥에서 들어오는 입력이다
 * (저장된 값·원격 유저). 무엇이든 받으면 화이트리스트 검증(불변식 7)이 무너지고, 서버도
 * 같은 `parseParts` 를 쓴다. 목록이 고정이면 검증은 그대로고, **그림이 있는 것만** 보인다
 * (`pickableOptions` 가 `hasArt` 로 거른다) — 안 쓰는 빈 자리는 아이 눈에 안 띈다.
 *
 * 자리가 모자라면 여기에 숫자만 더 늘리면 된다.
 */

/**
 * ⚠️ 머리모양(h*)·표정(f*)은 **일부러 없앴다. 되살리지 말 것.**
 *
 * 레이어 합성 시절의 잔재였는데, 지금 아바타는 "그 아이가 그 옷을 입은" 통짜 룩 그림
 * (`hero.look.<몸>_<옷>`)이다. 얼굴과 머리카락이 그 그림에 이미 그려져 있어서 표정을 따로
 * 얹으면 **얼굴이 둘**이 된다(모자 오버레이에서 겪은 것과 같은 문제 — OUTFIT_BUILTIN 참고).
 * 표정·머리모양을 다양화하려면 파츠가 아니라 **아이(body)를 늘리는 게 맞다**.
 *
 * 관리자 화면에서 "머리(head)"와 "머리·모자(hair)"가 나란히 떠 헷갈리던 것도 같이 정리됐다.
 * 예전에 저장된 파츠 문자열(`b2_h3_f2_c1_o3_a1`)은 그대로 읽힌다 — parseParts 가 모르는
 * prefix 를 무시하기 때문(전방호환). 저장할 때 4토큰 정규형으로 다시 쓰인다.
 */

// 커스터마이즈 카테고리(조합 가능한 레이어). prefix 1글자 = 토큰 식별자.
export const PART_CATEGORIES = ["body", "hair", "outfit", "acc"] as const;

/**
 * 🔴 **겹쳐 그리는 순서**(뒤 → 앞). 위 `PART_CATEGORIES` 와 **다르다** —
 * 저건 저장 토큰 차례(`b2_c1_o3_a1`)고 이건 화면에 쌓는 차례다.
 *
 * 아이 캐릭터를 그리는 곳이 둘이다: 화면 곳곳의 SVG(`HeroAvatar`)와 체험공간 캔버스
 * (`WorldScene`). 예전엔 이 배열이 **양쪽에 따로**(`ZORDER` · `ZO`) 적혀 있었다.
 * 한쪽만 고치면 **아무 에러 없이** 아이 캐릭터가 마을에서만 다르게 보인다 —
 * 모자가 머리 뒤로 들어가거나 옷이 몸에 가리는 식으로. CLAUDE.md 가 경고하던 바로 그 모양이다.
 */
export const HERO_LAYER_ORDER = ["body", "outfit", "acc", "hair"] as const;
export type PartCategory = (typeof PART_CATEGORIES)[number];
const PREFIX_OF: Record<PartCategory, string> = {
  body: "b", hair: "c", outfit: "o", acc: "a",
};
const CATEGORY_OF: Record<string, PartCategory> = {
  b: "body", c: "hair", o: "outfit", a: "acc",
};

export type BodyId = (typeof HERO_CATALOG.body)[number];
export type OutfitId = (typeof HERO_CATALOG.outfit)[number];
/** 모자·소품 id — **열려 있다**(운영자가 만든 c9·a12 …). 그릴 수 있는지는 매니페스트가 정한다. */
export type HairId = string;
export type AccId = string;
export type PaletteId = (typeof HERO_CATALOG.palette)[number];

export interface HeroParts {
  body: BodyId;
  hair: HairId;
  outfit: OutfitId;
  acc: AccId;
}

export const DEFAULT_PARTS: HeroParts = {
  body: "b1", hair: "c0", outfit: "o0", acc: "a0",
};
export const DEFAULT_BASE: HeroBase = "explorer_a";
export const DEFAULT_PALETTE: PaletteId = "warm";

/** 기본 히어로(폴백·신규). code 는 서버 동기화 시에만 채워진다. */
export function defaultHero(): SavedHero {
  return {
    version: HERO_SCHEMA_VERSION,
    base: DEFAULT_BASE,
    parts: stringifyParts(DEFAULT_PARTS),
    palette: DEFAULT_PALETTE,
    updatedAt: "1970-01-01T00:00:00.000Z", // 호출부에서 저장 시 갱신
  };
}

const has = <T extends readonly string[]>(arr: T, v: unknown): v is T[number] =>
  typeof v === "string" && (arr as readonly string[]).includes(v);

/**
 * 히어로 base 로 쓸 수 있는 값인가 — **목록이 아니라 모양으로** 검사한다.
 *
 * ## 왜 목록을 버렸나
 * 운영자가 만들 수 있는 캐릭터가 넷으로 묶여 있던 이유가 이 목록이었다. 그런데 이 값이
 * 실제로 하는 일은 하나뿐이다: **매니페스트 키 한 조각**(hero.base.<id>)이 되는 것.
 * 목록에 없으면 그림을 못 찾고, 그러면 usesPresetArt 가 거짓이 되어 **직접 꾸미기 룩으로
 * 폴백한다.** 모르는 값이 와도 최악이 「기본 아바타로 보인다」이다.
 *
 * ## 그래서 무엇을 지키나(불변식 7)
 * 검사할 것은 **모양**이다 — 이 문자열이 키·텍스처 이름 말고 다른 게 되지 못하게:
 *  · 슬래시·점·콜론을 못 쓴다 → 경로 타고 나가기·프로토콜 주입 불가
 *  · 길이 상한 → 키가 부풀지 않는다
 *  · 소문자·숫자·밑줄만 → 매니페스트 키 규약 그대로
 * 마을 친구·장식 스프라이트가 이미 같은 방식이다(mapLoader 의 spriteId).
 * 콘텐츠가 늘어나는 자리에 목록을 두면 **콘텐츠를 코드가 막게 된다.**
 *
 * 원격 유저가 보낸 값도 여기를 지난다(서버 ZoneRoom). 모르는 id 면 다른 아이 화면에서
 * 그 아바타가 기본 모습으로 보일 뿐, 아무것도 깨지지 않는다.
 */
const HERO_ID_RE = /^[a-z][a-z0-9_]{0,31}$/;
/** 매니페스트 키에서 캐릭터 id 를 뽑는다. */
const HERO_BASE_KEY_RE = /^hero[.]base[.]([a-z][a-z0-9_]{0,31})$/;
/** 매니페스트 키에서 (카테고리, 파츠 id) 를 뽑는다. */
const PART_KEY_RE = /^hero[.]([a-z]+)[.]([a-z][0-9]{1,3})$/;
export const isBase = (v: unknown): v is HeroBase =>
  typeof v === "string" && HERO_ID_RE.test(v);

/** 모자·소품 파츠 토큰(c1·a12)의 모양 — 카테고리 글자 + 숫자. */
const PART_TOKEN_RE = /^[a-z][0-9]{1,3}$/;
export const isPalette = (v: unknown): v is PaletteId =>
  has(HERO_CATALOG.palette, v);

/** "b2_h1_a3"(구버전) 또는 "b2_h1_f2_c1_o3_a3"(신버전) → HeroParts (화이트리스트 검증).
 *  토큰 가변·하위호환: 없는 카테고리는 기본값, 모르는 prefix는 무시(전방호환). 손상 시 null. */
export function parseParts(parts: unknown): HeroParts | null {
  if (typeof parts !== "string") return null;
  if (!/^[a-z]\d+(_[a-z]\d+)*$/.test(parts)) return null;
  const out: Partial<HeroParts> = {};
  for (const tok of parts.split("_")) {
    const prefix = tok[0]!;
    const cat = CATEGORY_OF[prefix];
    if (!cat) continue; // 모르는 카테고리 → 무시(미래 카테고리 전방호환)
    // 모자·소품은 **열려 있다**(운영자가 만든 c9·a12 …). 목록 대신 모양만 본다 —
    // 그림이 없으면 heroOverlayKeys 가 만든 키가 매니페스트에 없어서 아무것도 안 그려진다.
    // 몸·옷은 통짜 룩이 몸×옷 16장으로 고정이라 목록 그대로 둔다(없는 조합 = 그릴 그림 없음).
    const open = cat === "hair" || cat === "acc";
    if (open ? !PART_TOKEN_RE.test(tok) : !has(HERO_CATALOG[cat], tok)) return null;
    (out[cat] as string) = tok;
  }
  return { ...DEFAULT_PARTS, ...out };
}

export function stringifyParts(p: HeroParts): string {
  return PART_CATEGORIES.map((c) => p[c]).join("_");
}

/** 카테고리의 옵션 id → 매니페스트 키(`hero.body.b2`). 관리자 업로드/렌더 공용. */
export function heroPartKey(cat: PartCategory, id: string): string {
  return `hero.${cat}.${id}`;
}
/**
 * 그림이 **있으면 안 되는** 파츠 칸 — 비어 있는 게 정답이다.
 *
 * - `c0`·`a0`: 고르기의 "안 씀". 그림을 올려도 `heroOverlayKeys` 가 건너뛴다(안 나온다).
 * - `o0`: 기본 옷. 아이 그림(`hero.body.b*`)에 이미 그려져 있어서 또 얹으면 옷이 둘이 된다.
 *
 * 관리자에서 이 칸을 업로드 칸으로 보여 주면 "왜 여기만 비었지?" 하고 채우게 된다 —
 * 채워도 화면엔 안 나오고, 운영자는 뭘 잘못했는지 모른다. 그래서 아예 안 보여 준다.
 */
export function heroPartImageless(cat: PartCategory, id: string): boolean {
  if (cat === "outfit") return id === "o0";
  return (OVERLAY_NONE as Record<string, string>)[cat] === id;
}

/** 완성형 프리셋 캐릭터 이미지 키(`hero.base.explorer_a`). 있으면 레이어 합성 대신 이 이미지를 아바타로 사용. */
export function heroBaseKey(base: string): string {
  return `hero.base.${base}`;
}
/** 완성형 프리셋 탐험가 목록(custom 제외) — 웹 커스터마이저 라벨·관리자 프리셋 관리 공용 단일 출처.
 *  각 프리셋은 정지 이미지 `hero.base.<id>` + 방향 걷기 스트립 `hero.base.<id>.down/side/up` 을 갖는다. */
export const HERO_BASE_PRESETS: readonly { id: string; label: string }[] = [
  { id: "explorer_a", label: "탐험가 1" },
  { id: "explorer_b", label: "탐험가 2" },
  { id: "explorer_c", label: "탐험가 3" },
  { id: "explorer_d", label: "탐험가 4" },
  { id: "explorer_e", label: "탐험가 5" },
] as const;

/**
 * 그 중 **운영자가 직접 채우는 자리.**
 *
 * 아이 히어로의 `base` 는 밖에서 들어오는 입력이라 화이트리스트로 검증한다(불변식 7).
 * 목록을 열어 두면 그 검증이 무너지므로, 자리를 미리 만들어 두고 그림만 채우게 한다.
 *
 * **비어 있는 자리는 아무 화면에도 안 나온다** — 아이 화면은 물론(`pickableBases`)
 * 관리자 캐릭터 목록에서도 뺀다(`charRoster`). 늘 떠 있는 빈 카드 넷은 "덜 만든 것"으로
 * 보이는데, 자리는 원래 비어 있는 게 정상이다. 대신 [+ 새 친구] 하나로 만들고 지운다.
 */
/** 운영자가 만든 캐릭터인가(기본 제공 5명·직접 꾸미기는 아니다). */
export function isCustomHeroBase(id: string): boolean {
  return id !== "custom" && !(HERO_CATALOG.base as readonly string[]).includes(id);
}

/**
 * 매니페스트에 그림이 있는 **운영자가 만든 캐릭터** id 들.
 *
 * 목록을 코드에 두지 않는다 — **그림 한 장이 곧 항목이다.** 올리면 생기고 지우면 없어진다.
 * 서 있는 그림(hero.base.<id>)이 있는 것만 센다: 걷기 그림만 있는 자리는 아직 캐릭터가
 * 아니다(고르기 화면에 이름만 뜨고 그림이 없다).
 */
export function customHeroIds(assetKeys: Iterable<string>): string[] {
  const out: string[] = [];
  for (const k of assetKeys) {
    const m = HERO_BASE_KEY_RE.exec(k);
    if (m && m[1] && isCustomHeroBase(m[1])) out.push(m[1]);
  }
  return out.sort();
}

/** 같은 방식으로 찾은 **운영자가 만든 모자·소품** id(hero.hair.c9 → c9). */
export function customPartIds(cat: PartCategory, assetKeys: Iterable<string>): string[] {
  const known = HERO_CATALOG[cat] as readonly string[];
  const out: string[] = [];
  for (const k of assetKeys) {
    const m = PART_KEY_RE.exec(k);
    if (m && m[1] === cat && m[2] && !known.includes(m[2])) out.push(m[2]);
  }
  return out.sort((x, y) => Number(x.slice(1)) - Number(y.slice(1)));
}

/** 기본 제공 + 운영자가 만든 것 — 화면이 보여줄 전체 목록(기본 제공 먼저). */
export function allPartIds(cat: PartCategory, assetKeys: Iterable<string>): string[] {
  return [...(HERO_CATALOG[cat] as readonly string[]), ...customPartIds(cat, assetKeys)];
}

/**
 * 다음에 쓸 빈 id — 안 쓰는 **가장 작은 번호**(c5·a9 …).
 * 지운 자리를 다시 쓰므로 번호가 끝없이 커지지 않는다.
 */
export function nextPartId(cat: PartCategory, assetKeys: Iterable<string>): string {
  const taken = new Set(allPartIds(cat, assetKeys));
  const p = PREFIX_OF[cat];
  for (let n = 1; n <= 999; n++) if (!taken.has(p + n)) return p + n;
  throw new Error("자리를 더 만들 수 없어요(999개)");
}

/** 다음에 쓸 캐릭터 id — hero_1, hero_2 … 기본 제공(explorer_*)과 겹치지 않는다. */
export function nextHeroBaseId(assetKeys: Iterable<string>): string {
  const taken = new Set([
    ...(HERO_CATALOG.base as readonly string[]),
    ...customHeroIds(assetKeys),
  ]);
  for (let n = 1; n <= 999; n++) if (!taken.has("hero_" + n)) return "hero_" + n;
  throw new Error("캐릭터를 더 만들 수 없어요(999개)");
}

/** 직접 꾸미기 통짜 룩 이미지 키(`hero.look.b1_o2` = 몸 b1이 옷 o2를 자연스럽게 입은 완성 이미지).
 *  오버레이 합성 대신 (몸×옷) 조합을 통째로 생성한 이미지를 아바타로 사용(자연스러운 착장). */
export function heroLookKey(body: string, outfit: string): string {
  return `hero.look.${body}_${outfit}`;
}
/** 완주 메달 오버레이 키(FR-RALLY-10). 랠리 완주 시 히어로 가슴에 얹는 보상 뱃지. */
export function heroMedalKey(): string {
  return "hero.badge.medal";
}

/**
 * 통짜 룩·프리셋 이미지 **위에** 얹는 오버레이 카테고리(뒤→앞).
 *
 * 몸·옷·표정은 룩 이미지(hero.look.<몸>_<옷>)에 이미 그려져 있어 따로 얹으면 이중이 된다.
 * 모자(hair)·소품(acc)만 별개 그림으로 골라 얹는다 — 각 오버레이는 룩과 **같은 정사각 프레임**에
 * 제자리(머리 위·가슴 옆)로 그려져 있어서, 룩/걷기 스트립 어느 쪽 위에 같은 사각형으로 올려도 맞는다.
 *
 * ⚠️ 커스터마이저·SVG 아바타(HeroAvatar)·월드 캔버스(WorldScene) 세 렌더러가 이 목록을 함께 쓴다.
 *    한 곳만 고치면 미리보기와 월드의 모습이 갈린다.
 */
export const LOOK_OVERLAY_CATEGORIES = ["acc", "hair"] as const;
export type OverlayCategory = (typeof LOOK_OVERLAY_CATEGORIES)[number];

/** 각 오버레이 카테고리의 "안 씀" 값 — 이 값이면 아무것도 얹지 않는다. */
export const OVERLAY_NONE: Record<OverlayCategory, string> = { acc: "a0", hair: "c0" };

/** 완주 보상 전용 소품 — 커스터마이저에서 고를 수 없다.
 *  랠리를 끝내면 hero.badge.medal 이 자동으로 걸리므로, 고를 수 있게 두면 메달이 둘이 된다. */
export const REWARD_ONLY_ACC = "a4";

/**
 * 옷 그림에 **이미 그려져 있는** 것 — 그 위에 같은 종류를 또 얹으면 모자가 둘이 된다.
 * o0 기본(맨머리), o1 겨울(털모자·목도리), o2 탐험가(모자·가방), o3 우비(후드는 **내려져** 있다).
 * 옷 그림을 바꿀 때 이 표도 같이 봐야 한다(옷에 모자가 생기면 여기 추가).
 *
 * ⚠️ o3(우비)는 예전에 `["hair"]` 였다. 앞모습만 후드를 쓰고 옆·뒤는 벗은 상태여서
 * 걸을 때 후드가 씌워졌다 벗겨졌다 했다(2026-07-28 발견). **네 방향 모두 내린 쪽으로 통일**하고
 * — 뒷모습에 후드를 씌우면 80px 화면에서 이목구비 없는 노란 덩어리가 된다 —
 * 머리에 아무것도 없으니 모자를 쓸 수 있게 열었다.
 */
export const OUTFIT_BUILTIN: Record<string, readonly OverlayCategory[]> = {
  o0: [],
  o1: ["hair"],
  o2: ["hair", "acc"],
  o3: [],
};

/** 이 옷 위에 이 오버레이를 얹어도 되는가(옷에 이미 있으면 안 된다). */
export function overlayAllowed(cat: OverlayCategory, outfit: string): boolean {
  return !(OUTFIT_BUILTIN[outfit] ?? []).includes(cat);
}

/** 히어로 하나에 얹을 오버레이 이미지 키(뒤→앞). "안 씀"과 옷에 이미 있는 것은 건너뛴다. */
export function heroOverlayKeys(parts: HeroParts): string[] {
  return LOOK_OVERLAY_CATEGORIES.filter(
    (cat) =>
      parts[cat] !== OVERLAY_NONE[cat] &&
      overlayAllowed(cat, parts.outfit) &&
      // 완주 메달은 **소품이 아니다** — 아래 설명
      !(cat === "acc" && parts.acc === REWARD_ONLY_ACC),
  ).map((cat) => heroPartKey(cat, parts[cat]));
}

/**
 * ⚠️ **완주 메달(a4)을 소품으로 되살리지 말 것.**
 *
 * 메달은 원래 소품 한 칸이었는데(`hero.acc.a4`), 지금은 랠리를 끝내면 자동으로 걸리는
 * **보상 뱃지**(`hero.badge.medal`)가 따로 있다. 둘 다 그리면 **메달이 둘**이 된다.
 *
 * 더 나쁜 건 지울 수가 없다는 것이다. 커스터마이저는 a4 를 선택지에서 빼 두는데
 * (`REWARD_ONLY_ACC`), 그래서 이미 a4 를 들고 있는 히어로는 — 예전에 저장된 값이나
 * 공유 링크(`?h=`)로 온 값 — **그 칸을 바꿀 방법이 없다.** 탐험가 옷(o2)처럼 소품 단계가
 * 아예 사라지는 조합이면 더더욱. "완주도 안 했는데 메달이 계속 보이고 없어지지도 않는다"가
 * 이것이었다(2026-07-31).
 *
 * 그래서 **그리지 않는다.** 파츠 문자열에 a4 가 남아 있어도 무해하고(하위호환),
 * 진짜 완주자는 뱃지 경로로 정상적으로 메달을 받는다.
 */

/** 완성형 프리셋 그림(hero.base.<id>)을 쓰는가.
 *  프리셋은 그 자체로 완성된 캐릭터라 모자·소품을 얹지 않는다 — 세 렌더러가 같은 판정을 써야 한다. */
export function usesPresetArt(base: string, hasArt: (key: string) => boolean): boolean {
  return hasArt(heroBaseKey(base));
}
export { PREFIX_OF };

/**
 * 별명 길이 상한.
 *
 * 🔴 **화면만 알고 있던 수였다.** 서버(`filterNickname`)는 길이를 안 봐서, 화면을 거치지
 * 않은 요청은 200자짜리 별명도 그대로 저장됐다 — 선생님 화면 한 줄이 통째로 밀리고
 * 인쇄물도 망가진다. 이제 `serverNickname` 이 서버에서 같은 수로 자른다.
 */
export const MAX_NICK = 16;

/**
 * 닉네임 클라이언트측 1차 정리(FR-HERO-02). 길이 제한 + 제어문자 제거.
 * ⚠️ 금칙어·개인정보 필터의 권위는 서버(FR-MULTI-04/S2-2). 여기선 최소 정리만.
 */
/**
 * 🔴 **서버가 별명을 받아들이는 유일한 문**(불변식 7 — 밖에서 온 값).
 *
 * 예전엔 서버가 `filterNickname` 만 불렀다. 그건 금칙어만 본다 — **길이도 제어문자도
 * 안 본다.** 화면(`sanitizeNickname`)은 16자로 자르고 제어문자를 지우는데 서버는
 * 안 지웠으니, 화면을 거치지 않은 요청 하나면 200자짜리·줄바꿈 든 별명이 그대로 들어왔다.
 * 그 별명은 **선생님 화면·전시장·인쇄물**에 아이 이름표로 뜬다.
 *
 * 「권위는 서버」라고 적어 두는 것으로는 서버가 검사하게 되지 않는다 —
 * 실제로 그렇게 적힌 주석이 있었는데 서버는 안 봤다.
 *
 * 순서가 중요하다: **자르고 나서** 금칙어를 본다. 먼저 보면 잘려 나갈 자리의 말 때문에
 * 멀쩡한 별명이 거부된다.
 */
export function serverNickname(
  input: unknown,
  badwords: readonly string[] = DEFAULT_BADWORDS,
): string | null {
  const clean = sanitizeNickname(input);
  if (!clean) return null;
  return filterNickname(clean, badwords);
}

export function sanitizeNickname(input: unknown): string | undefined {
  if (typeof input !== "string") return undefined;
  // 제어문자(개행·널 등, U+0000–U+001F, U+007F) 제거 후 트림.
  // eslint-disable-next-line no-control-regex
  const cleaned = input.replace(/[\u0000-\u001F\u007F]/g, "").trim();
  if (!cleaned) return undefined;
  return cleaned.slice(0, MAX_NICK);
}

/**
 * 외부 입력(localStorage·?h=·서버 코드)을 안전한 SavedHero 로 정규화(FR-HERO-15/08).
 * 어떤 필드든 손상·미지값이면 기본값으로 폴백. 절대 throw 하지 않는다.
 */
export function sanitizeHero(input: unknown): SavedHero {
  const base = defaultHero();
  if (!input || typeof input !== "object") return base;
  const o = input as Record<string, unknown>;

  const parts = parseParts(o.parts);
  const result: SavedHero = {
    version: HERO_SCHEMA_VERSION,
    base: isBase(o.base) ? o.base : DEFAULT_BASE,
    parts: parts ? stringifyParts(parts) : base.parts,
    palette: isPalette(o.palette) ? o.palette : DEFAULT_PALETTE,
    nickname: sanitizeNickname(o.nickname),
    updatedAt:
      typeof o.updatedAt === "string" && o.updatedAt
        ? o.updatedAt
        : base.updatedAt,
  };
  if (isValidHeroCode(o.code)) {
    result.code = o.code;
  }
  return result;
}

/**
 * 자리 조절이 가능한 오버레이 키 전체(정면 기준) — 엣지·관리자가 같은 목록을 본다.
 *
 * ⚠️ **여기(hero.ts)에 두는 이유**: heroOverlayFit.ts 에 두면 카탈로그를 import 해야 하는데,
 * `node --experimental-strip-types` 로 도는 스크립트(`hero:check`)가 확장자 없는 상대 import 를
 * 못 읽어 통째로 죽는다. 카탈로그가 있는 이 파일에 두면 import 자체가 필요 없다.
 */
export function overlayFitKeys(assetKeys: Iterable<string> = []): string[] {
  return [
    ...LOOK_OVERLAY_CATEGORIES.flatMap((cat) =>
      // 기본 제공 + **운영자가 만든 것**. 안 넣으면 만든 모자가 자리 맞추기 목록에 아예 안 뜬다
      // — 운영자는 「왜 이 모자만 못 맞추지」를 겪는다(2026-08-23 신고의 나머지 절반).
      allPartIds(cat, assetKeys)
        .filter((id) => id !== OVERLAY_NONE[cat] && !(cat === "acc" && id === REWARD_ONLY_ACC))
        .map((id) => heroPartKey(cat, id)),
    ),
    // 완주 메달 — 아래 설명
    heroMedalKey(),
  ];
}

/**
 * ⚠️ 이 목록은 **운영자가 자리를 맞출 수 있는 것**이자, 저장된 조절값이 살아남는 화이트리스트다
 * (`sanitizeOverlayFitDoc` 가 이걸로 거른다). 그래서 화면에 그려지는 것과 **정확히 같아야 한다.**
 *
 * 어긋나면 아무 에러 없이 조용히 헛돈다. 실제로 둘 다 있었다(2026-07-31):
 *  - 목록에 **완주 메달이 없어서** 관리자에서 크기를 조절해도 아무 일도 안 일어났다
 *    (저장은 되는 것처럼 보이는데 정화 단계에서 버려졌다)
 *  - 목록에 **a4 가 있어서** 아무도 안 그리는 칸을 조절하고 있었다(메달은 뱃지로 그린다)
 */
